// pages/OrderHistory.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrderHistory, fetchTableList } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { displayPrice } from '../utils/general';
import { OrderSummary, Tables } from '../utils/types';
import CancelOrderButton from '../components/CancelOrderButton';
import GoToTop from '../components/GoToTop';
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";

interface HomeProps {
    setDisableOverflow: React.Dispatch<React.SetStateAction<boolean>>;
}

// const TOTAL_ORDERS_PER_PAGE = 5; // Replace with dynamic value if available from the API

const OrderHistory: React.FC<HomeProps> = ({ setDisableOverflow }) => {

    useEffect(() => {
        // Disable overflow for this page
        setDisableOverflow(true);

        // Optionally, you can reset overflow state when leaving the page
        return () => setDisableOverflow(false); // Optional cleanup when leaving page
    }, [setDisableOverflow]);

    const [totalOrdersPerPage, setTotalOrdersPerPage] = useState<number>(5);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setTotalOrdersPerPage(3); // Mobile
            } else {
                setTotalOrdersPerPage(5); // Desktop
            }
        };
        // console.log(window.innerWidth, totalOrdersPerPage);
        // Set the initial value based on the current window size
        handleResize();

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [totalOrdersPerPage]);

    const { settings } = useSettings();
    const navigate = useNavigate();
    const [orders, setOrders] = useState<OrderSummary[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [status, setStatus] = useState<string>('pending'); // Default status filter
    const [loading, setLoading] = useState<boolean>(true);

    const [filteredOrders, setFilteredOrders] = useState<OrderSummary[]>([]);

    const [showNow, setShowNow] = useState<boolean>(false); // New state for filtering "Now"

    const [tables, setTables] = useState<Tables[]>([]); // State to hold table data


    useEffect(() => {

        const fetchTables = async () => {
            try {
                const tableData = await fetchTableList();
                // console.log(tableData);
                setTables(tableData || []); // Adjust based on your API response structure
            } catch (error) {
                console.error('Error fetching table list:', error);
            }
        };


        const fetchOrders = async () => {
            setLoading(true);
            try {
                if (settings) {
                    // const data = await fetchOrderHistory(settings.vendorId, status, page, TOTAL_ORDERS_PER_PAGE);
                    const data = await fetchOrderHistory(settings.vendorId, status, page, totalOrdersPerPage);

                    // console.log(data);
                    // const totalPages = data.totalOrders / TOTAL_ORDERS_PER_PAGE;
                    const totalPages = data.totalOrders / totalOrdersPerPage;
                    setTotalPages(Math.ceil(totalPages));
                    // console.log(data, totalPages);

                    setOrders(data.orders); // Adjust to match the actual response structure
                }
            } catch (error) {
                console.error("Error fetching order history:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTables();

        fetchOrders();

    }, [settings, status, page, totalOrdersPerPage]);


    // Helper to get table name by ID
    const getTableName = (tableId: number | null): string => {
        if (tableId === null || tableId === 0) return ''; // Add a check for null or 0 'N/A';
        const table = tables.find((t) => t.id === tableId);
        return table ? table.tableName + ' (' + table.location + ')' : `Unknown (${tableId})`;
    };


    useEffect(() => {
        if (showNow) {
            // Filter orders to only include today's orders
            const today = new Date();
            const startOfDay = new Date(today.setHours(0, 0, 0, 0));
            const endOfDay = new Date(today.setHours(23, 59, 59, 999));

            const todayOrders = orders.filter(order => {
                const orderDate = new Date(order.created_at);
                return orderDate >= startOfDay && orderDate <= endOfDay;
            });
            setFilteredOrders(todayOrders);
            // const totalPages = todayOrders.totalOrders / TOTAL_ORDERS_PER_PAGE;
            // setTotalPages(Math.ceil(totalPages));
            // console.log(todayOrders);
        } else {
            setFilteredOrders(orders);

            // console.log(orders);

        }
    }, [showNow, orders]);

    const handleOrderClick = (orderId: number) => {
        navigate(`/order-details/${orderId}`);
    };

    const scrollableDivRef = useRef<HTMLDivElement>(null);

    return (
        <div className='w-full m-1'>
            <div className='flex flex-row justify-between'>

                <h2 className='pageTitle'>Order History</h2>
                {/* Buttons to toggle "Now" and "History" */}
                <div className="flex gap-4  text-lg">
                    <button
                        className={`ActionBtn bg-green-600  ${showNow ? 'text-blue-800 underline' : 'text-green-800'}`}
                        onClick={() => setShowNow(true)}
                    >
                        Now
                    </button>
                    <button
                        className={`ActionBtn bg-blue-600 ${!showNow ? 'text-blue-800 underline' : 'text-green-800'}`}
                        onClick={() => setShowNow(false)}
                    >
                        History
                    </button>
                </div>
            </div>

            <div className="flex flex-row gap-4">
                <div>
                    <fieldset className="flex gap-4">
                        <legend className='text-sm font-semibold text-blue-800 py-2'>Status:</legend>
                        {/* {['empty', 'occupied', 'paid', 'pending', 'completed', 'cancel'].map((option) => ( */}
                        {['paid', 'pending', 'cancel'].map((option) => (
                            <label key={option} className="flex items-center gap-2 text-sm font-normal text-cyan-800 pb-4 shadow">
                                <input
                                    type="radio"
                                    name="status"
                                    value={option}
                                    checked={status === option}
                                    onChange={(e) => setStatus(e.target.value)}
                                />
                                {option === 'empty' ? 'Empty' : option.charAt(0).toUpperCase() + option.slice(1)}
                            </label>
                        ))}
                    </fieldset>
                </div>
            </div>

            <div >
                <div className='w-full overflow-y-scroll pb-5' ref={scrollableDivRef}>

                    {loading ? (
                        <p>Loading Orders History List...</p>
                    ) : (
                        <>
                            <ul className='flex flex-row w-full list-none bg-gray-200 py-4 text-sm font-semibold'>
                                <li className='w-full flex'>
                                    <p className='w-1/5 px-2 py-0 text-xs'>Order ID</p>
                                        <p className='w-1/5 px-2 py-0 text-xs'>Order Type</p>
                                        <p className='w-1/5 px-2 py-0 text-xs'>Table</p>
                                    <p className='w-1/5 px-2 py-0 text-xs'>Total Amount</p>
                                    <p className='w-1/5 px-2 py-0 text-xs'>Date Updated</p>
                                    {/* <p className='w-1/5 px-2 py-0 text-xs text-center'>Status</p> */}

                                    {/* <p className='w-1/5 px-2 py-0 text-xs'>Created on</p> */}
                                    {/* <p className='w-1/5 px-2 py-0 text-xs'>Action</p> */}
                                </li>
                            </ul>
                            <ul className='flex flex-col w-full list-decimal [&>*:nth-child(odd)]:bg-green-100 [&>*:nth-child(even)]:bg-white'>

                                {filteredOrders.map((order) => (
                                    <li key={order.id} onClick={() => handleOrderClick(order.id)} className='p-4 w-full flex  border border-b-1 border-b-blue-400'  >
                                        <p className='w-1/5 px-2 py-0 text-xs'>#{order.id}</p>
                                        <p className='w-1/5 px-2 py-0 text-xs'>{order.order_type}</p>
                                        <p className='w-1/5 px-2 py-0 text-xs'>{getTableName(order.table_id)}</p>
                                        <p className='w-1/5 px-2 py-0 text-xs'>{displayPrice(order.amount_to_pay)}</p>
                                        <p className='w-1/5 px-2 py-0 text-xs'>
                                            {order.status.toUpperCase()}
                                            <br />
                                            <br />
                                                    {new Date(order.updated_at).toLocaleDateString()} {new Date(order.updated_at).toLocaleTimeString()}
                                                </p>

                                                {/* <div className='w-1/5 px-2 py-0 text-xs text-center'>
                                            {order.status.toUpperCase()}

                                            {!(order.status === 'paid' || order.status === 'pending' || order.status === 'cancel') && (

                                                <CancelOrderButton orderId={order.id} />

                                            )}

                                        </div> */}
                                                {/* <p className='w-1/5 px-2 py-0 text-xs'>{new Date(order.created_at).toLocaleDateString()}</p> */}
                                                {/* <p className='w-1/5 px-2 py-0 text-xs'>view</p> */}
                                            </li>
                                            ))
                                }
                                        </ul >
                                        <p className='w-full text-center font-bold  pt-2 pb-2'>Now is page {page} of {totalPages} pages</p>
                                    </>
                                )}

                                <GoToTop parentRef={scrollableDivRef} />

                                <div className='w-full flex justify-between fixed bottom-16 md:bottom-48 z-40 '>
                                    <button
                                        className={`${page === 1 ? 'bg-gray-600' : 'bg-green-600'} ActionBtn opacity-50 hover:opacity-80`}
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        <FaAngleLeft size={20} />
                                    </button>
                                    <button
                                        className={`${page === totalPages ? 'bg-gray-600' : 'bg-green-600'} ActionBtn opacity-50 hover:opacity-80`}
                                        disabled={page === totalPages}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        <FaAngleRight size={20} />
                                    </button>
                                </div>

                            </div >
                        </div>
                </div >
                );
};

                export default OrderHistory;
